import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { listStaff } from "../../api/staffApi";
import { createLookupApi } from "../../api/lookupApi";
import { listShiftTeacherAssigns, assignShiftTeacher, deleteShiftTeacherAssign } from "../../api/shiftTeacherAssignApi";
import "../Attendance/Attendance.css";
import "../Users/UsersPage.css";

const shiftApi = createLookupApi("shifts");

/**
 * Direct port of digicampusbd's `staff-assign-shift-teacher` ("Shift
 * Assign") — new service/controller on top of the already-existing
 * `ShiftTeacherAssign` entity/repository (added earlier this session to
 * support `staff-attendance-report-monthly-details`'s filter). Confirmed
 * via `ShiftTeacherAssignService` this is effectively 1:1 (one active shift
 * per staff, enforced in application code, not a DB constraint) — unlike
 * the many-to-many `ClassTeacherAssign`/`SubjectAssignToTeacher`. Building
 * this screen also lets real institute data exist in `shift_teacher_assign`
 * going forward, for testing the attendance-report page against.
 */
export default function ShiftTeacherAssignPage() {
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [shiftId, setShiftId] = useState("");
  const [assigns, setAssigns] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadAssigns = () => listShiftTeacherAssigns().then(setAssigns);

  useEffect(() => {
    listStaff().then(setStaffList);
    shiftApi.list().then((list) => {
      setShifts(list);
      if (list.length > 0) setShiftId(String(list[0].id));
    });
    loadAssigns();
  }, []);

  const toggleStaff = (id) => setSelectedStaffIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleAssign = async () => {
    setError("");
    setSuccess("");
    if (selectedStaffIds.length === 0 || !shiftId) {
      setError("Please select at least one staff member and a shift.");
      return;
    }
    try {
      await assignShiftTeacher(selectedStaffIds, shiftId);
      setSuccess("Assigned.");
      setSelectedStaffIds([]);
      loadAssigns();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to assign.");
    }
  };

  const handleDelete = async (assignId) => {
    try {
      await deleteShiftTeacherAssign(assignId);
      loadAssigns();
    } catch (err) {
      setError("Failed to remove assignment.");
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Shift Assign</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      {success && <p style={{ color: "var(--color-success, green)" }}>{success}</p>}
      <div className="attendance-filters">
        <label>
          Shift
          <select value={shiftId} onChange={(e) => setShiftId(e.target.value)}>
            {shifts.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={handleAssign}>
          Assign
        </button>
      </div>
      <div className="form-row">
        <span className="admit-card-label">Staff:</span>{" "}
        {staffList.map((s) => (
          <label key={s.staffId} className="checkbox-inline" style={{ display: "inline-flex", marginRight: 12 }}>
            <input type="checkbox" checked={selectedStaffIds.includes(String(s.staffId))} onChange={() => toggleStaff(String(s.staffId))} />
            {s.staffName}
          </label>
        ))}
      </div>
      <div className="table-scroll">
        <table className="attendance-table">
          <thead>
            <tr>
              <th>Staff</th>
              <th>Shift</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {assigns.map((a) => (
              <tr key={a.assignId}>
                <td>{a.staffName}</td>
                <td>{a.shiftName}</td>
                <td>
                  <button type="button" className="btn-link danger" onClick={() => handleDelete(a.assignId)}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {assigns.length === 0 && (
              <tr>
                <td colSpan={3}>No shift assignments yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </MainLayout>
  );
}
